import { useTranslation } from 'react-i18next';
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { useWorkDescription } from '@os/hooks/useWorkDescription';

export interface WorkDescriptionFieldProps {
  /**
   * Prefix for the DOM id + data-testids. Defaults to the Général field's ids so
   * that panel stays byte-identical; the Compte panel passes its own prefix so
   * the two never collide when both are mounted or queried.
   */
  idPrefix?: string;
}

/**
 * "Meilleure description de votre travail ?" — the shared, persisted work-context
 * dropdown (Général → Profil AND Compte → Profil, claude.ai parity).
 *
 * SINGLE SOURCE OF TRUTH: it reads/writes through the one `useWorkDescription`
 * hook, which is keyed on the shared `['agent','preferences']` query. So both
 * panels render the SAME stored value and both save through the SAME
 * `PUT /agent/preferences { workDescription }` endpoint — change it in Général
 * and Compte reflects it (and vice-versa) via the shared cache, with no
 * duplicated, drift-prone local state. Labels come from the `os` namespace
 * (`settings.general.work.*`), reused verbatim in both panels — the options are
 * fixed, brand-neutral job families (no client / molecule / indication).
 */
export function WorkDescriptionField({
  idPrefix = 'settings-general-work',
}: WorkDescriptionFieldProps) {
  const { t } = useTranslation('os');
  const work = useWorkDescription();
  const selectId = `${idPrefix}-select`;

  return (
    <div>
      <label className="block" htmlFor={selectId}>
        <span className="mb-1 flex items-center gap-2 font-data text-[9px] uppercase tracking-[0.2em] text-ink-2">
          {t('settings.general.work.label')}
          {work.saving && (
            <Loader2
              className="h-3 w-3 animate-spin text-state-ink"
              data-testid={`${idPrefix}-saving`}
              aria-label={t('settings.general.work.saving')}
            />
          )}
          {work.saved && (
            <span
              role="status"
              data-testid={`${idPrefix}-saved`}
              className="inline-flex items-center gap-0.5 text-signal-ink"
            >
              <Check className="h-3 w-3" strokeWidth={2} aria-hidden /> {t('settings.general.work.saved')}
            </span>
          )}
        </span>
        <select
          id={selectId}
          data-testid={idPrefix}
          value={work.value}
          disabled={work.loading || work.saving}
          onChange={(e) => work.save(e.target.value)}
          className="w-full rounded-control border border-border bg-surface-2 px-2.5 py-1.5 text-[0.85rem] text-foreground outline-none focus:border-signal/60 disabled:opacity-60"
        >
          <option value="">{t('settings.general.work.placeholder')}</option>
          {work.options.map((id) => (
            <option key={id} value={id}>
              {t(`settings.general.work.options.${id}`)}
            </option>
          ))}
        </select>
      </label>
      {work.error && (
        <p
          role="alert"
          data-testid={`${idPrefix}-error`}
          className="mt-1 flex items-center gap-1 text-[0.78rem] text-destructive"
        >
          <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {t('settings.general.work.error')}
        </p>
      )}
    </div>
  );
}
